import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { badge_chat_dock_path } from "../routes"

/**
 * chat_dock_controller — manages the dock window toggle/close, plus a badge
 * resync safety net.
 *
 * Responsibilities:
 *  - toggle / close the dock window (CSS class on the dock element)
 *  - resync the unread badge from the server on every Turbo Drive navigation
 *    and when the tab regains visibility, so a missed Turbo Stream broadcast
 *    (dropped cable connection, deploy restart, etc.) can't leave the badge
 *    stuck — the #chat-dock element is data-turbo-permanent, so Turbo Drive
 *    navigation alone never refreshes it.
 *
 * Float panel lifecycle (open, restore, close) is managed entirely by the
 * Preact store inside chat_dock_react_wrap_controller + FloatPanel.jsx.
 * Do NOT create turbo-frame float panels here — that was the old approach.
 */
export default class extends Controller {
  static targets = ["window", "toggle"]
  static values  = { collapsed: Boolean }

  connect() {
    this._onVisibilityChange = () => {
      if (document.visibilityState === "visible") this._resyncBadge()
    }
    document.addEventListener("turbo:load", this._resyncBadge)
    document.addEventListener("visibilitychange", this._onVisibilityChange)
  }

  disconnect() {
    document.removeEventListener("turbo:load", this._resyncBadge)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
  }

  // ── Dock window ────────────────────────────────────────────────────────────

  toggle() {
    this.collapsedValue = !this.collapsedValue
    this._applyCollapsed()
  }

  close() {
    this.collapsedValue = true
    this._applyCollapsed()
  }

  // ── Private ────────────────────────────────────────────────────────────────

  _applyCollapsed() {
    const collapsed = this.collapsedValue
    this.element.classList.toggle("is-open", !collapsed)

    if (this.hasWindowTarget) {
      this.windowTarget.classList.toggle("chat-dock-window--open", !collapsed)
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", String(!collapsed))
    }
  }

  _resyncBadge = async () => {
    try {
      const response = await fetch(badge_chat_dock_path(), {
        headers: { "Accept": "text/vnd.turbo-stream.html" },
        credentials: "same-origin",
      })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch {
      // Non-fatal — the next navigation or visibility change will retry.
    }
  }
}
